import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Icon } from '@src/components/Icon';
import { Label } from '@src/components/Typography';
import { VStack } from '@src/components/VStack';
import identity from 'lodash/identity';
import { DateTime } from 'luxon';
import React, { ReactElement } from 'react';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { BASE_TEXT_FIELD_HEIGHT } from '../TextField/util';
import { DateFieldProps } from './interface';

export const DateField = ({
  formatOnChange = identity,
  helperText,
  label,
  labelStyle = 'stacked',
  maxDate,
  minDate,
  name,
  parseValue = identity,
  shouldDisableDate,
  disabled = false,
  placeholder = 'MM/DD/YYYY',
}: DateFieldProps): ReactElement => {
  const { control } = useFormContext();

  const getValue = (fieldValue: string | undefined) => {
    const parsedValue = parseValue(fieldValue);
    const value = parsedValue ? DateTime.fromFormat(parsedValue, 'yyyy-MM-dd') : parsedValue;
    return value;
  };

  const getHelperText = (error: FieldError | undefined) => {
    const parsedError = error || undefined;
    return parsedError ? parsedError.message : helperText;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value: fieldValue }, fieldState: { error } }) => (
          <VStack>
            {labelStyle === 'stacked' && <Label size="s">{label}</Label>}
            <MobileDatePicker
              closeOnSelect={true}
              disabled={disabled}
              maxDate={maxDate}
              minDate={minDate}
              onChange={(value) => {
                const dateValue = value ? value.toISODate() : value;
                const newValue = formatOnChange(dateValue, fieldValue);
                return onChange(newValue);
              }}
              shouldDisableDate={shouldDisableDate}
              slotProps={{
                actionBar: {
                  hidden: true,
                },
                textField: {
                  error: !!error,
                  fullWidth: true,
                  helperText: getHelperText(error),
                  InputLabelProps: { shrink: true },
                  InputProps: {
                    endAdornment: <Icon name="calendar" />,
                    sx: {
                      boxSizing: 'border-box',
                      fontSize: '14px',
                      height: `${BASE_TEXT_FIELD_HEIGHT}px`,
                    },
                  },
                  label: labelStyle === 'inline' ? label : undefined,
                  placeholder,
                },
                toolbar: {
                  hidden: true,
                },
                dialog: {
                  sx: {
                    '& .MuiPickersDay-today': {
                      backgroundColor: 'grey.300', //theme.palette.grey['300']
                      borderRadius: '50%',
                      '&:not(.Mui-selected)': {
                        border: 'none !important',
                      },
                    },
                    '& .MuiDialogActions-root': {
                      display: 'none', //hide buttons
                    },
                    '& .PrivatePickersSlideTransition-root': {
                      minHeight: '235px',
                    },
                  },
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  cursor: 'pointer',
                },
                '& .MuiOutlinedInput-root input': {
                  cursor: 'text',
                },
              }}
              value={getValue(fieldValue)}
            />
          </VStack>
        )}
      />
    </LocalizationProvider>
  );
};
